import type {
  FieldDefinition,
  FieldState,
  TypedFieldValue,
  EntityReference,
} from '@/components/objects/typedFieldData';
import type { Attachment } from '@/lib/attachments';
import type { ObjectActivityEntry } from '@/lib/objectActivity';
export const OBJECTS_HOME = '/design/objects';
export const objectPath = (id: string) => OBJECTS_HOME + '/object/' + encodeURIComponent(id);
export const PEOPLE: EntityReference[] = [
  { id: 'person-204', label: 'Лея Орлова', kind: 'user', path: objectPath('person-204') },
  { id: 'person-205', label: 'Марк Лесной', kind: 'user', path: objectPath('person-205') },
];
export const STATUSES = [
  { id: 'new', label: 'Новая', tone: 'neutral' as const },
  { id: 'active', label: 'В работе', tone: 'info' as const },
  { id: 'done', label: 'Завершена', tone: 'success' as const },
];
export const IMPORTANCE = [
  { id: 'minor', label: 'Низкая', level: 1 as const },
  { id: 'major', label: 'Средняя', level: 2 as const },
  { id: 'critical', label: 'Высокая', level: 3 as const },
];
export const CATEGORIES = [
  { id: 'review', label: 'Проверка' },
  { id: 'request', label: 'Заявка' },
  { id: 'plan', label: 'Планирование' },
];
export const SAMPLE_FILE: Attachment = {
  id: 'guide',
  name: 'Памятка по проверке.pdf',
  mime: 'application/pdf',
  size: 81639,
  state: 'ready',
  url: '/design-fixtures/sample-invoice.pdf',
};
export function ready(type: TypedFieldValue['type'], value: unknown): FieldState {
  return { state: 'ready', value: { type, value } as TypedFieldValue };
}
export const CATALOG_DEFINITIONS: FieldDefinition[] = [
  { id: 'text', type: 'text', label: 'Текст', required: true, maxLength: 160 },
  { id: 'multiline', type: 'multiline', label: 'Многострочный текст' },
  { id: 'number', type: 'number', label: 'Число', min: -100, max: 100 },
  { id: 'money', type: 'money', label: 'Деньги', currency: 'RUB' },
  { id: 'date', type: 'date', label: 'Дата' },
  { id: 'period', type: 'period', label: 'Период' },
  { id: 'deadline', type: 'deadline', label: 'Срок', timeZone: 'Europe/Moscow' },
  { id: 'person', type: 'person', label: 'Человек', options: PEOPLE },
  { id: 'status', type: 'status', label: 'Статус', options: STATUSES },
  {
    id: 'select',
    type: 'select',
    label: 'Выбор из списка',
    options: Array.from({ length: 180 }, (_, i) => ({
      id: 'team-' + (i + 1),
      label: 'Группа ' + String(i + 1).padStart(3, '0'),
    })),
  },
  { id: 'tags', type: 'tags', label: 'Теги', maxTags: 8 },
  {
    id: 'entity',
    type: 'entity',
    label: 'Ссылка на объект',
    options: [
      { id: 'review-104', label: 'Проверка № 104', kind: 'task', path: objectPath('review-104') },
      { id: 'invoice-304', label: 'Счёт DEMO-304', kind: 'account', path: objectPath('invoice-304') },
    ],
  },
  { id: 'file', type: 'file', label: 'Файл' },
  { id: 'link', type: 'link', label: 'Ссылка' },
  { id: 'email', type: 'email', label: 'Почта' },
  { id: 'phone', type: 'phone', label: 'Телефон' },
  { id: 'checkbox', type: 'checkbox', label: 'Флажок' },
  { id: 'secret', type: 'secret', label: 'Секрет' },
  { id: 'importance', type: 'importance', label: 'Важность', options: IMPORTANCE },
  { id: 'code', type: 'custom:review-code', label: 'Поле сервиса', config: { prefix: 'DEMO' } },
  {
    id: 'readonly',
    type: 'text',
    label: 'Идентификатор',
    readOnly: true,
    help: 'Это свойство задаёт источник данных.',
  },
];
export const CATALOG_VALUES: Record<string, FieldState> = {
  text: ready('text', 'Проверка доступа'),
  multiline: ready('multiline', 'Сверить список участников.\nПодготовить результат к встрече.'),
  number: ready('number', 0),
  money: ready('money', { minorUnits: -1875000, currency: 'RUB' }),
  date: ready('date', '2026-10-28'),
  period: ready('period', { from: '2026-10-28', to: '2026-10-30' }),
  deadline: ready('deadline', '2026-10-30T15:00:00+03:00'),
  person: ready('person', PEOPLE[0]),
  status: ready('status', 'active'),
  select: ready('select', 'team-12'),
  tags: ready('tags', ['Осень', 'Проверка']),
  entity: ready('entity', {
    id: 'review-104',
    label: 'Проверка № 104',
    kind: 'task',
    path: objectPath('review-104'),
  }),
  file: ready('file', [SAMPLE_FILE]),
  link: ready('link', 'https://example.test/review'),
  email: ready('email', 'leya@example.test'),
  phone: ready('phone', '+7 000 000-00-00'),
  checkbox: ready('checkbox', false),
  secret: ready('secret', { configured: true }),
  importance: ready('importance', 'major'),
  code: ready('custom:review-code', 'DEMO-104'),
  readonly: ready('text', 'CAT-001'),
};
export interface DemoObject {
  id: string;
  title: string;
  kind: 'task' | 'user' | 'account';
  description: string;
  defaultMode: 'panel' | 'tab' | 'window';
  definitions: FieldDefinition[];
  values: Record<string, FieldState>;
  activity: ObjectActivityEntry[];
  files: Attachment[];
}
const head: FieldDefinition[] = [
  { id: 'status', type: 'status', label: 'Статус', options: STATUSES },
  { id: 'importance', type: 'importance', label: 'Важность', options: IMPORTANCE },
  { id: 'category', type: 'select', label: 'Тип', options: CATEGORIES },
  { id: 'owner', type: 'person', label: 'Ответственный', options: PEOPLE },
];
function initialActivity(id: string): ObjectActivityEntry[] {
  return [
    {
      id: id + '-created',
      kind: 'change',
      at: '2026-10-26T09:10:00+03:00',
      actor: { id: 'person-204', name: 'Лея Орлова' },
      changes: [{ key: 'created', label: 'Объект', before: '—', after: 'Создан' }],
    },
    {
      id: id + '-comment',
      kind: 'comment',
      thread: {
        message: {
          id: id + '-message',
          author: { id: 'person-205', name: 'Марк Лесной' },
          sentAt: '2026-10-26T10:20:00+03:00',
          content: {
            format: 'markdown',
            text: 'Добавил **памятку**. Результат проверки можно оставить в этой ветке.',
          },
          attachments: [SAMPLE_FILE],
          receipt: 'read',
        },
        replies: [
          {
            id: id + '-reply',
            author: { id: 'person-204', name: 'Лея Орлова' },
            sentAt: '2026-10-26T10:25:00+03:00',
            content: { format: 'plain', text: 'Хорошо, сверю значения перед встречей.' },
          },
        ],
      },
    },
  ];
}
function baseValues(): Record<string, FieldState> {
  return {
    status: ready('status', 'new'),
    importance: ready('importance', 'major'),
    category: ready('select', 'review'),
    owner: ready('person', null),
  };
}
export const INITIAL_OBJECTS: DemoObject[] = [
  {
    id: 'review-104',
    title: 'Проверка № 104',
    kind: 'task',
    description: 'Сверка доступа и материалов к встрече',
    defaultMode: 'panel',
    definitions: [
      ...head,
      { id: 'subject', type: 'text', label: 'Тема', required: true },
      { id: 'note', type: 'multiline', label: 'Описание' },
      { id: 'deadline', type: 'deadline', label: 'Срок', timeZone: 'Europe/Moscow' },
      { id: 'budget', type: 'money', label: 'Бюджет', currency: 'RUB' },
      { id: 'tags', type: 'tags', label: 'Теги' },
      { id: 'code', type: 'custom:review-code', label: 'Код проверки', config: { prefix: 'DEMO' } },
    ],
    values: {
      ...baseValues(),
      subject: ready('text', 'Проверка доступа'),
      note: ready('multiline', 'Сверить участников и подготовить памятку.'),
      deadline: ready('deadline', '2026-10-30T15:00:00+03:00'),
      budget: ready('money', { minorUnits: 1875000, currency: 'RUB' }),
      tags: ready('tags', ['Осень']),
      code: ready('custom:review-code', 'DEMO-104'),
    },
    activity: initialActivity('review-104'),
    files: [SAMPLE_FILE],
  },
  ...PEOPLE.map((person, i): DemoObject => ({
    id: person.id,
    title: person.label,
    kind: 'user',
    description: i ? 'Координатор · пример профиля' : 'Специалист · пример профиля',
    defaultMode: 'tab',
    definitions: [
      ...head,
      { id: 'department', type: 'text', label: 'Направление' },
      { id: 'email', type: 'email', label: 'Почта' },
      { id: 'phone', type: 'phone', label: 'Телефон' },
      { id: 'date', type: 'date', label: 'Дата начала' },
      { id: 'secret', type: 'secret', label: 'Код доступа' },
    ],
    values: {
      ...baseValues(),
      owner: ready('person', person),
      status: ready('status', 'active'),
      department: ready('text', i ? 'Координация' : 'Проверки'),
      email: ready('email', i ? 'mark@example.test' : 'leya@example.test'),
      phone: ready('phone', '+7 000 000-00-00'),
      date: ready('date', '2026-10-01'),
      secret: ready('secret', { configured: true }),
    },
    activity: initialActivity(person.id),
    files: [SAMPLE_FILE],
  })),
  {
    id: 'invoice-304',
    title: 'Счёт DEMO-304',
    kind: 'account',
    description: 'Услуги сопровождения · октябрь',
    defaultMode: 'window',
    definitions: [
      ...head,
      { id: 'amount', type: 'money', label: 'Сумма', currency: 'RUB' },
      { id: 'period', type: 'period', label: 'Период услуг' },
      { id: 'paid', type: 'checkbox', label: 'Оплачен' },
      {
        id: 'reference',
        type: 'entity',
        label: 'Основание',
        options: [
          { id: 'review-104', label: 'Проверка № 104', kind: 'task', path: objectPath('review-104') },
        ],
      },
    ],
    values: {
      ...baseValues(),
      category: ready('select', 'request'),
      amount: ready('money', { minorUnits: 4260000, currency: 'RUB' }),
      period: ready('period', { from: '2026-10-01', to: '2026-10-31' }),
      paid: ready('checkbox', false),
      reference: ready('entity', {
        id: 'review-104',
        label: 'Проверка № 104',
        kind: 'task',
        path: objectPath('review-104'),
      }),
    },
    activity: initialActivity('invoice-304'),
    files: [SAMPLE_FILE],
  },
];
